import useClient from "@/api/client";
import useGet from "@/composables/useGet";
import { useValidation } from "@/composables/useValidation";
import { computed, ref } from "vue";
import { z } from "zod";
import { ZBelege, ZBlatt } from "@/features/fahrer/api/useAuftrag";
import {
  FEHLT_NOCH,
  IST_ABGERECHNET,
  NICHT_ABGERECHNET,
  ZAbrechnungsstatus,
} from "./useAbrechnungsplan";

const ZFahrer = z.object({
  id: z.string(),
  anzeige_name: z.string(),
  abrechnungsstatus: ZAbrechnungsstatus,
  tageskontrollblatt: ZBlatt,
  belege: ZBelege,
});

export type IFahrer = z.infer<typeof ZFahrer>;
export type IBlatt = z.infer<typeof ZBlatt>;
export type IBeleg = z.infer<typeof ZBelege> extends (infer T)[] ? T : never;

const useAuftragFahrer = (auftragId: string, fahrerId: string) => {
  const client = useClient();
  const data = ref<IFahrer>();

  const { request, urlParams } = client.get(
    "/query/abrechnung_auftrag/{}/fahrer/{}/",
  );
  urlParams.value = [auftragId, fahrerId];
  const query = useGet(request, data);

  useValidation(data, ZFahrer.optional());

  const fahrer = computed(() => data.value);
  const blatt = computed(() => data.value?.tageskontrollblatt);
  const belege = computed(() => data.value?.belege);
  const zeiten = computed(() => (blatt.value ? blatt.value.zeiten : []));

  const abrechnungsColor = computed(() => {
    if (!fahrer.value) return "";
    switch (fahrer.value.abrechnungsstatus) {
      case IST_ABGERECHNET:
        return "border-green-600";
      case NICHT_ABGERECHNET:
        return "border-yellow-600";
      case FEHLT_NOCH:
        return "border-red-600";
      default:
        return "";
    }
  });

  return {
    query,
    fahrer,
    zeiten,
    blatt,
    belege,
    abrechnungsColor,
  };
};

export default useAuftragFahrer;
